import {
  createApi,
  fetchBaseQuery,
  retry,
} from "@reduxjs/toolkit/dist/query/react";
import { config } from "../../../config";
import {
  LocalStorageKeys,
  getTransformedDataForPagination,
} from "../../../utils";

export const pricingAPI = createApi({
  reducerPath: "pricingAPI",
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: config.api_url + "mm",
      prepareHeaders: (headers) => {
        const token = localStorage.getItem(LocalStorageKeys.authToken);

        // If we have a token set in state, let's assume that we should be passing it.
        if (token) {
          headers.set("authorization", `Bearer ${token}`);
        }

        return headers;
      },
    }),
    { maxRetries: 1 }
  ),
  tagTypes: ["AllPricing", "AllCosting"],
  endpoints: (builder) => ({
    // Get All Pricing API
    getAllPricing: builder.query({
      query: ({
        page = 0,
        pageSize = 10,
        sort_by = null,
        sort_order = null,
        bom_id = "",
        object_type = "",
        object_id = "",
        currency = ""
      }: {
        page: number;
        pageSize: number;
        sort_by?: string | null;
        sort_order?: string | null;
        bom_id?: string;
        currency?: string;
        object_type: string;
        object_id: string;
      }) => ({
        url: `/pricing${page !== null ? `?page_no=${page + 1}` : ""}${
          pageSize ? `&page_size=${pageSize}` : ""
        }${bom_id ? `&bom_id=${bom_id}` : ""}${
          sort_by ? `&sort_by=${sort_by}` : ""
        }${sort_order ? `&sort_order=${sort_order}` : ""}${
          object_type ? `&object_type=${object_type}` : ""
        }${object_id ? `&object_id=${object_id}` : ""}${
          currency ? `&currency=${currency}` : ""}`,
        method: "GET",
      }),
      transformResponse: (
        data: any,
        meta: { request: Request; response?: Response }
      ) => {
        return getTransformedDataForPagination(data, meta?.response);
      },
      providesTags: ["AllPricing"],
    }),

    // Get Pricing by id API
    getPricingById: builder.query({
      query: ({
        pricing_id = "",
        bom_id = "",
      }: {
        pricing_id: string;
        bom_id?: string;
      }) => ({
        url: `/pricing/${pricing_id}${bom_id ? `?bom_id=${bom_id}` : ""}`,
        method: "GET",
      }),
    }),

    // Create Pricing API
    createPricing: builder.mutation({
      query: ({ bom_id = "", payload }: { bom_id: string; payload: any }) => ({
        url: `/pricing${bom_id ? `?bom_id=${bom_id}` : ""}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["AllPricing"],
    }),

    // Update Pricing API
    updatePricing: builder.mutation({
      query: ({
        pricing_id = "",
        bom_id = "",
        payload,
      }: {
        pricing_id: string;
        bom_id?: string;
        payload: any;
      }) => ({
        url: `/pricing/${pricing_id}${bom_id ? `?bom_id=${bom_id}` : ""}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["AllPricing"],
    }),

    // Delete Pricing API
    deletePricing: builder.mutation({
      query: ({
        pricing_id = "",
        force = true,
      }: {
        pricing_id: string;
        force?: boolean;
      }) => ({
        url: `/pricing/${pricing_id}${force ? `?force=${force}` : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AllPricing"],
    }),

    // Get All Costing details API
    getAllCosting: builder.query({
      query: ({
        pricing_id = "",
        bom_id = "",
        page = 1,
        pageSize = 10,
        sort_by = null,
        sort_order = null,
      }: {
        pricing_id: string;
        bom_id: string;
        page?: number;
        pageSize?: number;
        sort_by?: string | null;
        sort_order?: string | null;
      }) => ({
        url: `/costing?bom_id=${bom_id}&pricing_id=${pricing_id}&page_no=${page}&page_size=${pageSize}${
          sort_by ? `&sort_by=${sort_by}` : ``
        }${sort_order ? `&sort_order=${sort_order}` : ``}`,
        method: "GET",
      }),
      providesTags: ["AllCosting"]
    }),

    // Costing Calculation API
    calculateCosting: builder.mutation({
      query: ({
        pricing_id = "",
        exchange_rates,
      }: {
        pricing_id: string;
        exchange_rates: any;
      }) => ({
        url: `/pricing/${pricing_id}/calculate`,
        method: "POST",
        body: { exchange_rates },
      }),
      invalidatesTags: ["AllCosting"]
    }),

    // Save Calculated Costing API
    saveCosting: builder.mutation({
      query: ({
        pricing_id = "",
        bom_id = "",
        payload,
      }: {
        pricing_id: string;
        bom_id: string;
        payload: any;
      }) => ({
        url: `/bom/${bom_id}/pricing/${pricing_id}/costing`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["AllCosting"]
    }),

    // Duplicate Pricing API
    duplicatePricing: builder.mutation({
      query: (pricing_id) => ({
        url: `/pricing/${pricing_id}`,
        method: "POST",
      }),
    }),

    // Delete Costing API
    deleteCosting: builder.mutation({
      query: ({
        costing_id = "",
        pricing_id = "",
        force = true,
      }: {
        costing_id?: string;
        pricing_id?: string;
        force?: boolean;
      }) => ({
        url: `/costing${force ? `?force=${force}` : ""}${costing_id ? `&costing_id=${costing_id}` : ""}${pricing_id ? `&pricing_id=${pricing_id}` : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AllCosting"]
    }),


  }),
});

export const {
  useLazyGetAllPricingQuery,
  useLazyGetPricingByIdQuery,
  useCreatePricingMutation,
  useUpdatePricingMutation,
  useDeletePricingMutation,
  useLazyGetAllCostingQuery,
  useCalculateCostingMutation,
  useSaveCostingMutation,
  useDuplicatePricingMutation,
  useDeleteCostingMutation
} = pricingAPI;
